import { useEffect, useState, useRef, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import {
  fetchUploadHistory,
  uploadBankFile,
  fetchUnmatchedRecords,
  resolveUnmatchedRecord,
  deleteBankUpload,
  deleteUnmatchedRecord,
  batchDeleteUnmatched,
} from '../../store/slices/financeSlice'
import type { BankFileType, UnmatchedStatus, MatchedRecord, BankFileUpload, UnmatchedRecord, DuplicateVirtualAccountGroup } from '@/types/finance'
import { getMatchedRecords, resetUploadMatches, getUnmatchedRecords, getDuplicateVirtualAccounts, getMatchableBills, reassignUnmatchedRecord } from '../../services/api/finance'
import { useToast } from '../../components/ToastContainer'
import { useFinancePageSize, FINANCE_PAGE_SIZE_OPTIONS } from '../../hooks/useFinancePageSize'
import usePermissions, { Permission } from '../../hooks/usePermissions'
import { FinanceAuditLog } from '../../components/finance'
import {
  BanknotesIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  TrashIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline'

const UNMATCHED_PAGE_SIZE_OPTIONS = FINANCE_PAGE_SIZE_OPTIONS

const fileTypeLabels: Record<BankFileType, string> = {
  PASSBOOK: '銀行存摺明細',
  PAYMENT_STATUS: '繳費狀態檔',
  ACH_RESULT: 'ACH結果檔',
}

const unmatchedStatusLabels: Record<UnmatchedStatus, string> = {
  PENDING: '待處理',
  MATCHED: '已匹配',
  IGNORED: '已忽略',
  SKIPPED: '已略過',
  VENDOR_MATCHED: '廠商款項',
  OTHER_RESOLVED: '已處理',
}

const unmatchedStatusColors: Record<UnmatchedStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  MATCHED: 'bg-green-100 text-green-800',
  IGNORED: 'bg-gray-100 text-gray-800',
  SKIPPED: 'bg-orange-100 text-orange-800',
  VENDOR_MATCHED: 'bg-purple-100 text-purple-800',
  OTHER_RESOLVED: 'bg-green-100 text-green-800',
}

const BankReconciliation = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()
  const { currentProject } = useAppSelector(state => state.project)
  const {
    uploadHistory,
    uploadHistoryPagination,
    uploadHistoryLoading,
    uploadHistoryError,
    uploading,
    unmatchedRecords,
    unmatchedPagination,
    unmatchedLoading,
  } = useAppSelector(state => state.finance)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const topScrollRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const [tableScrollWidth, setTableScrollWidth] = useState(0)
  const [selectedFileType, setSelectedFileType] = useState<BankFileType>('PASSBOOK')
  const [historyPage, setHistoryPage] = useState(1)
  const [unmatchedPage, setUnmatchedPage] = useState(1)
  const [unmatchedPageSize, setUnmatchedPageSize] = useFinancePageSize()
  const [unmatchedFilter, setUnmatchedFilter] = useState<UnmatchedStatus[]>([])
  const [unmatchedMonth, setUnmatchedMonth] = useState<string>(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [uploadHistoryExpanded, setUploadHistoryExpanded] = useState(false)
  const [uploadSectionExpanded, setUploadSectionExpanded] = useState(false)
  const [duplicateVAExpanded, setDuplicateVAExpanded] = useState(false)
  const [duplicateVAGroups, setDuplicateVAGroups] = useState<DuplicateVirtualAccountGroup[]>([])
  const [duplicateVALoading, setDuplicateVALoading] = useState(false)
  const [duplicateVATotal, setDuplicateVATotal] = useState<{ groups: number; records: number }>({ groups: 0, records: 0 })
  const [duplicateVAExpandedGroups, setDuplicateVAExpandedGroups] = useState<Set<string>>(new Set())
  const [duplicateVASet, setDuplicateVASet] = useState<Set<string>>(new Set())
  const [duplicateFirstByVA, setDuplicateFirstByVA] = useState<Map<string, string>>(new Map())
  const [duplicateRecordIds, setDuplicateRecordIds] = useState<Set<string>>(new Set())
  const [duplicateRangeMonths, setDuplicateRangeMonths] = useState<string>('12')
  const [duplicateVAMonthFilter, setDuplicateVAMonthFilter] = useState<string>('')
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false)
  const [duplicateSecondaryIds, setDuplicateSecondaryIds] = useState<string[]>([])
  const [reassignRecord, setReassignRecord] = useState<UnmatchedRecord | null>(null)
  const [reassignBills, setReassignBills] = useState<any[]>([])
  const [reassignLoading, setReassignLoading] = useState(false)
  const [reassignSubmitting, setReassignSubmitting] = useState(false)
  const [reassignSearch, setReassignSearch] = useState('')
  const [reassignSelectedBillId, setReassignSelectedBillId] = useState<string>('')
  const [matchedDetailRecord, setMatchedDetailRecord] = useState<UnmatchedRecord | null>(null)
  const [skippedModalUpload, setSkippedModalUpload] = useState<BankFileUpload | null>(null)
  const [skippedRecords, setSkippedRecords] = useState<UnmatchedRecord[]>([])
  const [unmatchedModalUpload, setUnmatchedModalUpload] = useState<BankFileUpload | null>(null)
  const [unmatchedModalRecords, setUnmatchedModalRecords] = useState<UnmatchedRecord[]>([])
  const [unmatchedModalLoading, setUnmatchedModalLoading] = useState(false)
  const [skippedLoading, setSkippedLoading] = useState(false)
  const [unmatchedSearch, setUnmatchedSearch] = useState('')
  const [unmatchedSearchDebounced, setUnmatchedSearchDebounced] = useState('')
  const [selectedRecords, setSelectedRecords] = useState<string[]>([])
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [resolvingRecord, setResolvingRecord] = useState<any>(null)
  const [resolveCandidates, setResolveCandidates] = useState<any[]>([])
  const [resolveCandidatesLoading, setResolveCandidatesLoading] = useState(false)
  const [resolveSearch, setResolveSearch] = useState('')
  // 匹配住戶帳單的帳期篩選（'' = 全部帳期，用於遲繳匹配舊帳單）
  const [resolvePeriod, setResolvePeriod] = useState('')
  const [resolveSelectedBillId, setResolveSelectedBillId] = useState<string>('')
  const [resolveSubmitting, setResolveSubmitting] = useState(false)
  const [resolveNote, setResolveNote] = useState('')
  // Vendor tab state
  const [resolveTab, setResolveTab] = useState<'BILL' | 'VENDOR' | 'OTHER'>('BILL')
  const [resolveVendors, setResolveVendors] = useState<Array<{ id: string; name: string; type: 'INCOME' | 'EXPENSE'; tags: string[] }>>([])
  const [resolveVendorsLoading, setResolveVendorsLoading] = useState(false)
  const [resolveSelectedVendorId, setResolveSelectedVendorId] = useState<string>('')
  const [resolveVendorSearch, setResolveVendorSearch] = useState('')

  // Matched records modal state
  const [showMatchedModal, setShowMatchedModal] = useState(false)
  const [selectedUpload, setSelectedUpload] = useState<BankFileUpload | null>(null)
  const [matchedRecords, setMatchedRecords] = useState<MatchedRecord[]>([])
  const [matchedRecordsLoading, setMatchedRecordsLoading] = useState(false)
  const [matchedPagination, setMatchedPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 })
  const [expandedRecords, setExpandedRecords] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchUploadHistory({ projectId: currentProject.id, params: { page: historyPage, limit: 10 } }))
    }
  }, [dispatch, currentProject?.id, historyPage])

  // 搜尋防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setUnmatchedSearchDebounced(unmatchedSearch)
      setUnmatchedPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [unmatchedSearch])

  const loadUnmatchedRecords = (page = unmatchedPage) => {
    if (!currentProject?.id) return

    dispatch(fetchUnmatchedRecords({
      projectId: currentProject.id,
      params: {
        page,
        limit: unmatchedPageSize,
        status: unmatchedFilter.length > 0 ? (unmatchedFilter.join(',') as any) : undefined,
        keyword: unmatchedSearchDebounced || undefined,
        fileType: 'PASSBOOK',
        month: unmatchedMonth || undefined,
        recordIds: showOnlyDuplicates ? Array.from(duplicateRecordIds).join(',') : undefined,
      },
    }))
  }

  const refreshUnmatchedRecords = (page = unmatchedPage) => {
    const targetPage = Math.max(1, page)

    if (targetPage !== unmatchedPage) {
      setUnmatchedPage(targetPage)
      return
    }

    loadUnmatchedRecords(targetPage)
  }

  useEffect(() => {
    loadUnmatchedRecords(unmatchedPage)
  }, [dispatch, currentProject?.id, unmatchedPage, unmatchedPageSize, unmatchedFilter, unmatchedSearchDebounced, unmatchedMonth, showOnlyDuplicates, duplicateRecordIds])

  // 量測表格寬度並同步上方捲動條
  useEffect(() => {
    const el = tableScrollRef.current
    if (!el) return
    const update = () => setTableScrollWidth(el.scrollWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [unmatchedRecords])

  const handleTopScroll = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft
    }
  }

  const handleTableScroll = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft
    }
  }

  const loadDuplicateVirtualAccounts = async () => {
    if (!currentProject?.id) return
    setDuplicateVALoading(true)
    try {
      const res = await getDuplicateVirtualAccounts(currentProject.id, {
        rangeMonths: duplicateRangeMonths,
      })
      if (res.success && res.data) {
        setDuplicateVAGroups(res.data.groups)
        setDuplicateVATotal({ groups: res.data.totalGroups, records: res.data.totalRecords })
        setDuplicateVASet(new Set(res.data.groups.map(g => g.virtualAccount)))
        const firstMap = new Map<string, string>()
        const allIds = new Set<string>()
        const secondaryIds: string[] = []
        for (const g of res.data.groups) {
          const sorted = [...g.records].sort(
            (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
          )
          if (sorted.length > 0) {
            firstMap.set(g.virtualAccount, sorted[0].id)
            // 第 2 筆以後 = 重複繳款
            for (let i = 1; i < sorted.length; i++) secondaryIds.push(sorted[i].id)
          }
          for (const r of g.records) allIds.add(r.id)
        }
        setDuplicateFirstByVA(firstMap)
        setDuplicateRecordIds(allIds)
        setDuplicateSecondaryIds(secondaryIds)
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入重複虛擬帳號')
    } finally {
      setDuplicateVALoading(false)
    }
  }

  // 跨月偵測：依設定的範圍載入重複虛擬帳號（與主列表月份無關）
  useEffect(() => {
    if (currentProject?.id) {
      loadDuplicateVirtualAccounts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, duplicateRangeMonths])

  const openReassignModal = async (record: UnmatchedRecord) => {
    setReassignRecord(record)
    setReassignSelectedBillId('')
    // 預設用已匹配帳單的戶號搜尋；若沒有，留空讓使用者自己搜
    const defaultSearch = record.matchedBill?.unit?.unitCode || ''
    setReassignSearch(defaultSearch)
    await loadReassignBills(defaultSearch)
  }

  const loadReassignBills = async (search: string) => {
    if (!currentProject?.id) return
    setReassignLoading(true)
    try {
      const res = await getMatchableBills(currentProject.id, {
        search: search || undefined,
        limit: 50,
      })
      if (res.success && res.data) {
        setReassignBills(res.data.bills || [])
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入可匹配的帳單')
    } finally {
      setReassignLoading(false)
    }
  }

  const closeReassignModal = () => {
    setReassignRecord(null)
    setReassignBills([])
    setReassignSearch('')
    setReassignSelectedBillId('')
  }

  const handleReassignSubmit = async () => {
    if (!currentProject?.id || !reassignRecord || !reassignSelectedBillId) return
    setReassignSubmitting(true)
    try {
      await reassignUnmatchedRecord(currentProject.id, reassignRecord.id, {
        billId: reassignSelectedBillId,
      })
      showSuccess('已重新指派到目標帳單')
      closeReassignModal()
      refreshUnmatchedRecords()
      loadDuplicateVirtualAccounts()
    } catch (error: any) {
      showError('重新指派失敗', error.message || '請稍後再試')
    } finally {
      setReassignSubmitting(false)
    }
  }

  const toggleDuplicateVAGroup = (va: string) => {
    setDuplicateVAExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(va)) next.delete(va)
      else next.add(va)
      return next
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getVirtualAccount = (rawData: string): string | null => {
    try {
      const data = JSON.parse(rawData)
      return data.virtualAccount || null
    } catch {
      return null
    }
  }

  const getInputType = (rawData: string): string | null => {
    try {
      const data = JSON.parse(rawData)
      return data.inputType || null
    } catch {
      return null
    }
  }

  const getTransactionSummary = (rawData: string): string | null => {
    try {
      const data = JSON.parse(rawData)
      return data.transactionSummary || null
    } catch {
      return null
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !currentProject?.id) return

    try {
      await dispatch(uploadBankFile({
        projectId: currentProject.id,
        fileType: selectedFileType,
        file,
      })).unwrap()
      showSuccess('檔案上傳成功', `已處理 ${file.name}`)
      // Refresh both lists
      dispatch(fetchUploadHistory({ projectId: currentProject.id, params: { page: 1, limit: 10 } }))
      setHistoryPage(1)
      refreshUnmatchedRecords(1)
    } catch (error: any) {
      showError('上傳失敗', error.message || '請稍後再試')
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openResolveModal = async (record: any) => {
    setResolvingRecord(record)
    setResolveNote('')
    setResolveSearch('')
    setResolveSelectedBillId('')
    setResolveCandidates([])
    setResolveSelectedVendorId('')
    setResolveVendorSearch('')
    // 預設帶入頁面右上角選擇的月份
    const initialPeriod = unmatchedMonth || ''
    setResolvePeriod(initialPeriod)
    // 支出記錄無法匹配住戶帳單，預設廠商分頁；存入記錄預設帳單分頁
    const initialTab: 'BILL' | 'VENDOR' = record.direction === 'DEBIT' ? 'VENDOR' : 'BILL'
    setResolveTab(initialTab)
    setShowResolveModal(true)
    if (initialTab === 'BILL') {
      await loadResolveCandidates(record, '', initialPeriod)
    } else {
      await loadVendorsForResolve(record.direction || 'CREDIT')
    }
  }

  const loadResolveCandidates = async (record: any, search: string, period: string) => {
    if (!currentProject?.id) return
    setResolveCandidatesLoading(true)
    setResolveSelectedBillId('')
    try {
      const res = await getMatchableBills(currentProject.id, {
        search: search || undefined,
        billingPeriod: period || undefined,
        amount: Number(record.amount),
        limit: 50,
      })
      if (res.success && res.data) {
        setResolveCandidates(res.data.bills || [])
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入可匹配的帳單')
    } finally {
      setResolveCandidatesLoading(false)
    }
  }

  const handleResolveSubmit = async () => {
    if (!currentProject?.id || !resolvingRecord || !resolveSelectedBillId) return
    setResolveSubmitting(true)
    try {
      const note = resolveNote.trim() || `人工匹配：存摺 ${formatDate(resolvingRecord.transactionDate)} ${formatCurrency(resolvingRecord.amount)}`
      await dispatch(resolveUnmatchedRecord({
        projectId: currentProject.id,
        recordId: resolvingRecord.id,
        data: { action: 'LINK', billId: resolveSelectedBillId, note },
      })).unwrap()
      showSuccess('已匹配')
      closeResolveModal()
      refreshUnmatchedRecords()
    } catch (error: any) {
      const msg = typeof error === 'string' ? error : (error?.message || '請稍後再試')
      showError('匹配失敗', msg)
    } finally {
      setResolveSubmitting(false)
    }
  }

  const handleResolveAsOther = async () => {
    if (!currentProject?.id || !resolvingRecord) return
    const reason = resolveNote.trim()
    if (!reason) {
      showError('請填寫原因', '其他處理需要填寫處理原因說明')
      return
    }
    setResolveSubmitting(true)
    try {
      await dispatch(resolveUnmatchedRecord({
        projectId: currentProject.id,
        recordId: resolvingRecord.id,
        data: { action: 'OTHER', note: reason },
      })).unwrap()
      showSuccess('已標記為已處理')
      closeResolveModal()
      refreshUnmatchedRecords()
    } catch (error: any) {
      const msg = typeof error === 'string' ? error : (error?.message || '請稍後再試')
      showError('處理失敗', msg)
    } finally {
      setResolveSubmitting(false)
    }
  }

  const handleResolveAsVendor = async () => {
    if (!currentProject?.id || !resolvingRecord || !resolveSelectedVendorId) return
    setResolveSubmitting(true)
    try {
      const note = resolveNote.trim()
      await dispatch(resolveUnmatchedRecord({
        projectId: currentProject.id,
        recordId: resolvingRecord.id,
        data: { action: 'VENDOR', vendorId: resolveSelectedVendorId, note: note || undefined },
      })).unwrap()
      showSuccess('已標記為廠商款項')
      closeResolveModal()
      refreshUnmatchedRecords()
    } catch (error: any) {
      const msg = typeof error === 'string' ? error : (error?.message || '請稍後再試')
      showError('標記失敗', msg)
    } finally {
      setResolveSubmitting(false)
    }
  }

  const closeResolveModal = () => {
    setShowResolveModal(false)
    setResolvingRecord(null)
    setResolveNote('')
    setResolveSearch('')
    setResolvePeriod('')
    setResolveSelectedBillId('')
    setResolveCandidates([])
    setResolveTab('BILL')
    setResolveVendors([])
    setResolveSelectedVendorId('')
    setResolveVendorSearch('')
  }

  // 載入廠商列表（依存摺記錄方向預先過濾類型）
  const loadVendorsForResolve = async (direction: 'CREDIT' | 'DEBIT') => {
    if (!currentProject?.id) return
    setResolveVendorsLoading(true)
    try {
      const expectedType = direction === 'CREDIT' ? 'INCOME' : 'EXPENSE'
      const { getVendors } = await import('../../services/api/finance')
      const res = await getVendors(currentProject.id, { type: expectedType, limit: 500 })
      if (res.success && res.data) {
        setResolveVendors(res.data.vendors.map(v => ({ id: v.id, name: v.name, type: v.type, tags: v.tags })))
      }
    } catch {
      setResolveVendors([])
    } finally {
      setResolveVendorsLoading(false)
    }
  }

  const handleDeleteUpload = async (uploadId: string, matchedCount: number) => {
    if (!currentProject?.id) return

    if (matchedCount > 0) {
      showError('無法刪除', '此上傳記錄有已配對的帳單，請先重置匹配')
      return
    }

    if (!confirm('確定要刪除此上傳記錄嗎？相關的未匹配記錄也會一併刪除。')) return

    try {
      await dispatch(deleteBankUpload({
        projectId: currentProject.id,
        uploadId,
      })).unwrap()
      showSuccess('上傳記錄已刪除')
      refreshUnmatchedRecords(1)
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  const handleResetMatches = async (uploadId: string, matchedCount: number) => {
    if (!currentProject?.id) return

    if (matchedCount === 0) {
      showError('無需重置', '此上傳記錄沒有已匹配的帳單')
      return
    }

    if (!confirm(`確定要重置此上傳記錄的 ${matchedCount} 筆匹配嗎？\n\n已匹配的帳單將恢復為「未付款」狀態，您可以重新上傳檔案進行對帳。`)) return

    try {
      const response = await resetUploadMatches(currentProject.id, uploadId)
      if (response.success) {
        showSuccess('重置成功', `已重置 ${response.data?.resetCount ?? 0} 筆匹配記錄`)
        // Refresh upload history
        dispatch(fetchUploadHistory({ projectId: currentProject.id, params: { page: historyPage, limit: 10 } }))
      }
    } catch (error: any) {
      showError('重置失敗', error.message || '請稍後再試')
    }
  }

  const handleDeleteUnmatchedRecord = async (recordId: string) => {
    if (!currentProject?.id) return

    if (!confirm('確定要刪除此記錄嗎？')) return

    try {
      await dispatch(deleteUnmatchedRecord({
        projectId: currentProject.id,
        recordId,
      })).unwrap()
      showSuccess('記錄已刪除')
      refreshUnmatchedRecords()
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  const handleBatchDelete = async () => {
    if (!currentProject?.id || selectedRecords.length === 0) return

    if (!confirm(`確定要刪除選取的 ${selectedRecords.length} 筆記錄嗎？`)) return

    try {
      await dispatch(batchDeleteUnmatched({
        projectId: currentProject.id,
        recordIds: selectedRecords,
      })).unwrap()
      showSuccess(`已刪除 ${selectedRecords.length} 筆記錄`)
      setSelectedRecords([])
      refreshUnmatchedRecords()
    } catch (error: any) {
      showError('刪除失敗', error.message || '請稍後再試')
    }
  }

  const toggleSelectRecord = (recordId: string) => {
    setSelectedRecords(prev =>
      prev.includes(recordId)
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    )
  }

  // Handle clicking on matched count to show matched records
  const handleViewMatchedRecords = async (upload: BankFileUpload) => {
    if (upload.matchedCount === 0) return

    setSelectedUpload(upload)
    setShowMatchedModal(true)
    setMatchedRecordsLoading(true)
    setExpandedRecords(new Set())

    try {
      const response = await getMatchedRecords(currentProject!.id, upload.id, { page: 1, limit: 50 })
      if (response.success && response.data) {
        setMatchedRecords(response.data.records)
        setMatchedPagination(response.data.pagination)
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入匹配記錄')
    } finally {
      setMatchedRecordsLoading(false)
    }
  }

  const loadMoreMatchedRecords = async (page: number) => {
    if (!currentProject?.id || !selectedUpload) return

    setMatchedRecordsLoading(true)
    try {
      const response = await getMatchedRecords(currentProject.id, selectedUpload.id, { page, limit: 50 })
      if (response.success && response.data) {
        setMatchedRecords(response.data.records)
        setMatchedPagination(response.data.pagination)
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入匹配記錄')
    } finally {
      setMatchedRecordsLoading(false)
    }
  }

  const toggleRecordExpand = (recordId: string) => {
    setExpandedRecords(prev => {
      const newSet = new Set(prev)
      if (newSet.has(recordId)) {
        newSet.delete(recordId)
      } else {
        newSet.add(recordId)
      }
      return newSet
    })
  }

  const handleViewUnmatchedRecords = async (upload: BankFileUpload) => {
    if (!currentProject?.id || !upload.unmatchedCount) return
    setUnmatchedModalUpload(upload)
    setUnmatchedModalRecords([])
    setUnmatchedModalLoading(true)
    try {
      const response = await getUnmatchedRecords(currentProject.id, {
        uploadId: upload.id,
        status: 'PENDING',
        page: 1,
        limit: 500,
      })
      if (response.success && response.data) {
        setUnmatchedModalRecords(response.data.records as UnmatchedRecord[])
      }
    } catch (error: any) {
      showError('載入失敗', error?.response?.data?.error || error.message || '無法載入未匹配記錄')
    } finally {
      setUnmatchedModalLoading(false)
    }
  }

  const closeUnmatchedModal = () => {
    setUnmatchedModalUpload(null)
    setUnmatchedModalRecords([])
  }

  const handleViewSkippedRecords = async (upload: BankFileUpload) => {
    if (!currentProject?.id || !upload.skippedCount) return
    setSkippedModalUpload(upload)
    setSkippedRecords([])
    setSkippedLoading(true)
    try {
      const response = await getUnmatchedRecords(currentProject.id, {
        uploadId: upload.id,
        status: 'SKIPPED',
        page: 1,
        limit: 500,
      })
      if (response.success && response.data) {
        setSkippedRecords(response.data.records as UnmatchedRecord[])
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入略過記錄')
    } finally {
      setSkippedLoading(false)
    }
  }

  const closeSkippedModal = () => {
    setSkippedModalUpload(null)
    setSkippedRecords([])
  }

  const closeMatchedModal = () => {
    setShowMatchedModal(false)
    setSelectedUpload(null)
    setMatchedRecords([])
    setExpandedRecords(new Set())
  }

  // 依月份篩選重複虛擬帳號群組：只保留「在該月份至少有一筆交易」的群組，但 group.records 保留完整清單
  const filteredDuplicateVAGroups = useMemo(() => {
    if (!duplicateVAMonthFilter) return duplicateVAGroups
    return duplicateVAGroups.filter(g =>
      g.records.some(r => (r.transactionDate || '').slice(0, 7) === duplicateVAMonthFilter)
    )
  }, [duplicateVAGroups, duplicateVAMonthFilter])

  const filteredDuplicateVATotal = useMemo(() => ({
    groups: filteredDuplicateVAGroups.length,
    records: filteredDuplicateVAGroups.reduce((sum, g) => sum + g.records.length, 0),
  }), [filteredDuplicateVAGroups])

  // 衍生顯示樣式：重複虛擬帳號的第 1 筆=已匹配、第 2+ 筆=重複繳款、已重新指派=已指派月份
  type DupDisplay =
    | { kind: 'normal' }
    | { kind: 'matched_first' }
    | { kind: 'duplicate' }
    | { kind: 'reassigned' }

  const getDupDisplay = (recordId: string, virtualAccount: string | null, status: UnmatchedStatus, resolveNote?: string | null): DupDisplay => {
    if (!virtualAccount || !duplicateVASet.has(virtualAccount)) return { kind: 'normal' }
    if (resolveNote && resolveNote.startsWith('[重新指派]')) return { kind: 'reassigned' }
    const firstId = duplicateFirstByVA.get(virtualAccount)
    if (firstId === recordId) {
      return status === 'MATCHED' ? { kind: 'matched_first' } : { kind: 'normal' }
    }
    return status === 'MATCHED' ? { kind: 'duplicate' } : { kind: 'normal' }
  }

  const renderDupBadge = (display: DupDisplay, matchedBillUnitCode?: string | null, matchedBillPeriod?: string | null) => {
    if (display.kind === 'reassigned') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border-2 border-orange-400">
          已指派月份
          {matchedBillUnitCode && <span className="ml-1 font-mono">· {matchedBillUnitCode}</span>}
          {matchedBillPeriod && <span className="ml-1">{matchedBillPeriod}</span>}
        </span>
      )
    }
    if (display.kind === 'duplicate') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          重複繳款
        </span>
      )
    }
    return null
  }

  const pendingRecords = unmatchedRecords.filter(r => r.status === 'PENDING')
  const currentUnmatchedPage = unmatchedPagination?.page ?? unmatchedPage
  const currentUnmatchedLimit = unmatchedPagination?.limit ?? unmatchedPageSize

  const { hasPermission } = usePermissions(currentProject?.id)
  const canView = hasPermission(Permission.VIEW_FINANCE_RECONCILIATION)
  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto mt-12 text-center bg-white rounded-lg shadow">
        <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">無權限存取</h2>
        <p className="mt-2 text-sm text-gray-600">您沒有檢視銀行對帳的權限，請聯絡專案管理員。</p>
        <Link to={`/project/${projectKey}/finance`} className="inline-block mt-4 text-blue-600 hover:underline">回財務管理首頁</Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/project/${projectKey}/finance`}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <BanknotesIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">銀行對帳</h1>
              <p className="text-sm text-gray-500">上傳銀行檔案進行對帳作業</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">月份:</label>
          <input
            type="month"
            value={unmatchedMonth}
            onChange={(e) => { setUnmatchedMonth(e.target.value); setUnmatchedPage(1) }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          />
          {unmatchedMonth && (
            <button
              type="button"
              onClick={() => { setUnmatchedMonth(''); setUnmatchedPage(1) }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              清除
            </button>
          )}
        </div>
      </div>

      {/* Upload Section (collapsible) */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          onClick={() => setUploadSectionExpanded(v => !v)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
        >
          <h2 className="text-lg font-medium text-gray-900">上傳銀行檔案</h2>
          {uploadSectionExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" />}
        </button>
        {uploadSectionExpanded && (
        <div className="px-6 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              檔案類型
            </label>
            <select
              value={selectedFileType}
              onChange={(e) => setSelectedFileType(e.target.value as BankFileType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.entries(fileTypeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.xlsx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
              {uploading ? '上傳中...' : '選擇檔案上傳'}
            </button>
          </div>
        </div>

        <div className="text-sm text-gray-500">
          支援格式: CSV, TXT, XLSX。上傳後系統將自動比對帳單進行對帳。
        </div>
        </div>
        )}
      </div>

      {uploadHistoryError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{uploadHistoryError}</p>
        </div>
      )}

      {/* Upload History (collapsible) */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          onClick={() => setUploadHistoryExpanded(v => !v)}
          className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center space-x-3">
            <h2 className="text-lg font-medium text-gray-900">上傳紀錄</h2>
            {uploadHistoryPagination && uploadHistoryPagination.total > 0 && (
              <span className="text-sm text-gray-500">({uploadHistoryPagination.total} 筆)</span>
            )}
          </div>
          {uploadHistoryExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" />}
        </button>

        {uploadHistoryExpanded && (uploadHistoryLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : uploadHistory.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            尚無上傳紀錄
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      檔案類型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      檔案名稱
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      記錄數
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      已匹配
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      未匹配
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      已略過
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      上傳時間
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {uploadHistory.map((upload) => (
                    <tr key={upload.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {fileTypeLabels[upload.fileType]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex items-center">
                          <DocumentIcon className="w-4 h-4 text-gray-400 mr-2" />
                          {upload.fileName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {upload.recordCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {upload.matchedCount > 0 ? (
                          <button
                            onClick={() => handleViewMatchedRecords(upload)}
                            className="inline-flex items-center text-green-600 font-medium hover:text-green-800 hover:underline cursor-pointer"
                            title="點擊查看匹配詳情"
                          >
                            <EyeIcon className="w-4 h-4 mr-1" />
                            {upload.matchedCount}
                          </button>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {upload.unmatchedCount > 0 ? (
                          <button
                            onClick={() => handleViewUnmatchedRecords(upload)}
                            className="inline-flex items-center text-red-600 font-medium hover:text-red-800 hover:underline cursor-pointer"
                            title="點擊查看未匹配記錄"
                          >
                            <EyeIcon className="w-4 h-4 mr-1" />
                            {upload.unmatchedCount}
                          </button>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {(upload.skippedCount || 0) > 0 ? (
                          <button
                            onClick={() => handleViewSkippedRecords(upload)}
                            className="text-orange-600 font-medium hover:underline cursor-pointer"
                            title="點擊查看略過原因"
                          >
                            {upload.skippedCount}
                          </button>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(upload.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        {upload.matchedCount > 0 && (
                          <button
                            onClick={() => handleResetMatches(upload.id, upload.matchedCount)}
                            className="text-orange-600 hover:text-orange-900"
                            title="重置匹配 - 將已匹配帳單恢復為未付款狀態"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteUpload(upload.id, upload.matchedCount)}
                          className={`text-red-600 hover:text-red-900 ${upload.matchedCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={upload.matchedCount > 0 ? '有已配對帳單，請先重置匹配' : '刪除'}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {uploadHistoryPagination && uploadHistoryPagination.totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  第 {uploadHistoryPagination.page} / {uploadHistoryPagination.totalPages} 頁
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                    disabled={historyPage === 1}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setHistoryPage(p => Math.min(uploadHistoryPagination.totalPages, p + 1))}
                    disabled={historyPage === uploadHistoryPagination.totalPages}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        ))}
      </div>

      {/* 重複虛擬帳號繳款 (collapsible) */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50">
          <button
            onClick={() => setDuplicateVAExpanded(v => !v)}
            className="flex items-center space-x-3 flex-1 text-left"
          >
            <h2 className="text-lg font-medium text-gray-900">重複虛擬帳號繳款</h2>
            {duplicateVATotal.groups > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {duplicateVAMonthFilter
                  ? `${filteredDuplicateVATotal.groups} / ${duplicateVATotal.groups} 組 · ${filteredDuplicateVATotal.records} 筆`
                  : `${duplicateVATotal.groups} 組 / ${duplicateVATotal.records} 筆`}
              </span>
            )}
            <span className="text-xs text-gray-500">
              （同一虛擬帳號出現 2 次以上，可能是用舊帳號繳下個月的帳單）
            </span>
          </button>
          <div className="flex items-center space-x-2">
            <label className="text-xs text-gray-500">月份</label>
            <input
              type="month"
              value={duplicateVAMonthFilter}
              onChange={(e) => setDuplicateVAMonthFilter(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="text-sm px-2 py-1 border border-gray-300 rounded"
              placeholder="YYYY-MM"
            />
            {duplicateVAMonthFilter && (
              <button
                onClick={(e) => { e.stopPropagation(); setDuplicateVAMonthFilter('') }}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
                title="清除月份篩選"
              >
                清除
              </button>
            )}
            <label className="text-xs text-gray-500">回溯範圍</label>
            <select
              value={duplicateRangeMonths}
              onChange={(e) => setDuplicateRangeMonths(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="text-sm px-2 py-1 border border-gray-300 rounded"
            >
              <option value="3">3 個月</option>
              <option value="6">6 個月</option>
              <option value="12">1 年</option>
              <option value="24">2 年</option>
              <option value="all">全部時間</option>
            </select>
            <button
              onClick={() => setDuplicateVAExpanded(v => !v)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              {duplicateVAExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {duplicateVAExpanded && (
          <div className="p-4">
            {duplicateVALoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : filteredDuplicateVAGroups.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                {duplicateVAMonthFilter
                  ? `${duplicateVAMonthFilter} 月份沒有重複的虛擬帳號繳款`
                  : '此範圍內沒有重複的虛擬帳號繳款'}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      const allIds = filteredDuplicateVAGroups.map(g => g.virtualAccount)
                      const allOpen = allIds.every(id => duplicateVAExpandedGroups.has(id))
                      setDuplicateVAExpandedGroups(allOpen ? new Set() : new Set(allIds))
                    }}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {filteredDuplicateVAGroups.length > 0 && filteredDuplicateVAGroups.every(g => duplicateVAExpandedGroups.has(g.virtualAccount)) ? (
                      <><ChevronUpIcon className="w-4 h-4 mr-1" />全部收合</>
                    ) : (
                      <><ChevronDownIcon className="w-4 h-4 mr-1" />全部展開</>
                    )}
                  </button>
                </div>
                {filteredDuplicateVAGroups.map(group => {
                  const isOpen = duplicateVAExpandedGroups.has(group.virtualAccount)
                  return (
                    <div key={group.virtualAccount} className="border border-gray-200 rounded-md">
                      <button
                        onClick={() => toggleDuplicateVAGroup(group.virtualAccount)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-3 flex-1 flex-wrap gap-y-1">
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {group.virtualAccount}
                          </span>
                          {group.records.map((r, idx) => (
                            <span
                              key={r.id}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                idx === 0 ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {new Date(r.transactionDate).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                              {' '}
                              {new Date(r.transactionDate).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </span>
                          ))}
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            重複 {group.count} 次
                          </span>
                          <span className="text-sm text-gray-600">
                            合計 {formatCurrency(group.totalAmount)}
                          </span>
                        </div>
                        {isOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
                      </button>

                      {isOpen && (
                        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="text-xs text-gray-500 uppercase">
                                <th className="text-left py-2 pr-4">交易日期</th>
                                <th className="text-left py-2 pr-4">輸入行別</th>
                                <th className="text-left py-2 pr-4">交易摘要</th>
                                <th className="text-right py-2 pr-4">金額</th>
                                <th className="text-left py-2 pr-4">狀態</th>
                                <th className="text-left py-2 pr-4">已匹配帳單</th>
                                <th className="text-left py-2 pr-4">備註</th>
                                <th className="text-left py-2 pr-4">上傳檔案</th>
                                <th className="text-right py-2">操作</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.records.map(r => {
                                const canReassign = r.status === 'PENDING' || r.status === 'MATCHED'
                                const reassignRecordPayload: UnmatchedRecord = {
                                  id: r.id,
                                  uploadId: r.upload.id,
                                  rawData: r.rawData,
                                  amount: Number(r.amount),
                                  transactionDate: r.transactionDate,
                                  memo: r.memo || undefined,
                                  status: r.status,
                                  matchedBillId: r.matchedBill?.id || undefined,
                                  createdAt: r.transactionDate,
                                  upload: { id: r.upload.id, fileName: r.upload.fileName },
                                  matchedBill: r.matchedBill ? {
                                    id: r.matchedBill.id,
                                    billingPeriod: r.matchedBill.billingPeriod,
                                    totalAmount: Number(r.matchedBill.totalAmount),
                                    paidAmount: r.matchedBill.paidAmount != null ? Number(r.matchedBill.paidAmount) : undefined,
                                    paidAt: r.matchedBill.paidAt || undefined,
                                    bankRecord: r.matchedBill.bankRecord || null,
                                    paymentChannel: r.matchedBill.paymentChannel || undefined,
                                    reconciledAt: r.matchedBill.reconciledAt || undefined,
                                    unit: r.matchedBill.unit
                                      ? { id: '', unitCode: r.matchedBill.unit.unitCode, address: r.matchedBill.unit.address || undefined }
                                      : null,
                                  } : null,
                                }
                                return (
                                  <tr key={r.id} className="border-t border-gray-200">
                                    <td className="py-2 pr-4 whitespace-nowrap">{formatDate(r.transactionDate)}</td>
                                    <td className="py-2 pr-4 whitespace-nowrap font-mono text-gray-600">{getInputType(r.rawData) || '-'}</td>
                                    <td className="py-2 pr-4 whitespace-nowrap text-gray-600">{getTransactionSummary(r.rawData) || '-'}</td>
                                    <td className="py-2 pr-4 text-right font-medium whitespace-nowrap">{formatCurrency(Number(r.amount))}</td>
                                    <td className="py-2 pr-4">
                                      {(() => {
                                        const display = getDupDisplay(r.id, group.virtualAccount, r.status, r.resolveNote)
                                        const dupBadge = renderDupBadge(display, r.matchedBill?.unit?.unitCode, r.matchedBill?.billingPeriod)
                                        const clickable = r.status === 'MATCHED'
                                        if (dupBadge) {
                                          return clickable ? (
                                            <button
                                              type="button"
                                              onClick={() => setMatchedDetailRecord(reassignRecordPayload)}
                                              className="cursor-pointer hover:opacity-80"
                                              title="點擊查看匹配詳情"
                                            >
                                              {dupBadge}
                                            </button>
                                          ) : dupBadge
                                        }
                                        if (clickable) {
                                          return (
                                            <button
                                              type="button"
                                              onClick={() => setMatchedDetailRecord(reassignRecordPayload)}
                                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium hover:opacity-80 cursor-pointer ${unmatchedStatusColors[r.status]}`}
                                              title="點擊查看匹配詳情"
                                            >
                                              {unmatchedStatusLabels[r.status]}
                                              {r.matchedBill?.unit?.unitCode && (
                                                <span className="ml-1 font-mono">· {r.matchedBill.unit.unitCode}</span>
                                              )}
                                            </button>
                                          )
                                        }
                                        return (
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${unmatchedStatusColors[r.status]}`}>
                                            {unmatchedStatusLabels[r.status]}
                                          </span>
                                        )
                                      })()}
                                    </td>
                                    <td className="py-2 pr-4 text-xs">
                                      {r.matchedBill ? (
                                        <div>
                                          <div className="font-mono font-medium">{r.matchedBill.unit?.unitCode || '-'}</div>
                                          <div className="text-gray-500">{r.matchedBill.billingPeriod}</div>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="py-2 pr-4 text-gray-600 max-w-xs truncate" title={r.memo || ''}>{r.memo || '-'}</td>
                                    <td className="py-2 pr-4 text-xs text-gray-500 truncate max-w-[200px]" title={r.upload?.fileName}>{r.upload?.fileName}</td>
                                    <td className="py-2 text-right whitespace-nowrap">
                                      {canReassign && (
                                        <button
                                          onClick={() => openReassignModal(reassignRecordPayload)}
                                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200"
                                          title="重新指派到其他帳單"
                                        >
                                          <ArrowsRightLeftIcon className="w-3.5 h-3.5 mr-1" />
                                          重新指派
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 銀行存摺明細 - 主列表 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-medium text-gray-900">銀行存摺明細</h2>
              {unmatchedPagination && unmatchedPagination.total > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  共 {unmatchedPagination.total} 筆記錄
                </p>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center flex-wrap gap-2">
                <label
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer select-none transition-colors ${
                    showOnlyDuplicates
                      ? 'bg-red-100 text-red-800 border-transparent'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={showOnlyDuplicates}
                    onChange={(e) => { setShowOnlyDuplicates(e.target.checked); setUnmatchedPage(1) }}
                    className="h-3.5 w-3.5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  只顯示重複繳款 {duplicateRecordIds.size > 0 && `(${duplicateRecordIds.size})`}
                </label>
                <span className="text-sm text-gray-600 ml-2">狀態:</span>
                {(Object.entries(unmatchedStatusLabels) as [UnmatchedStatus, string][]).map(([key, label]) => {
                  const checked = unmatchedFilter.includes(key)
                  return (
                    <label
                      key={key}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer select-none transition-colors ${
                        checked
                          ? `${unmatchedStatusColors[key]} border-transparent`
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setUnmatchedFilter(prev =>
                            prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
                          )
                          setUnmatchedPage(1)
                        }}
                        className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      {label}
                    </label>
                  )
                })}
              </div>
              {selectedRecords.length > 0 && (
                <>
                  <button
                    onClick={handleBatchDelete}
                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                  >
                    <TrashIcon className="w-4 h-4 mr-2" />
                    刪除選取 ({selectedRecords.length})
                  </button>
                </>
              )}
            </div>
          </div>

          {/* 搜尋與每頁筆數 */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={unmatchedSearch}
                  onChange={(e) => setUnmatchedSearch(e.target.value)}
                  placeholder="搜尋虛擬帳號、備註、交易日期（2026-02-13 或 2026-02 或 02/13）..."
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">每頁顯示:</span>
              <select
                value={unmatchedPageSize}
                onChange={(e) => { setUnmatchedPageSize(Number(e.target.value)); setUnmatchedPage(1) }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {UNMATCHED_PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} 筆</option>
                ))}
              </select>
            </div>
            {(unmatchedSearch || unmatchedFilter.length > 0 || showOnlyDuplicates) && (
              <button
                onClick={() => { setUnmatchedSearch(''); setUnmatchedFilter([]); setShowOnlyDuplicates(false); setUnmatchedPage(1) }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
              >
                清除篩選
              </button>
            )}
          </div>

          {/* 上方翻頁列 */}
          {unmatchedPagination && unmatchedPagination.total > 0 && (
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-100">
              <div className="text-sm text-gray-700">
                顯示第 {((currentUnmatchedPage - 1) * currentUnmatchedLimit) + 1} - {Math.min(currentUnmatchedPage * currentUnmatchedLimit, unmatchedPagination.total)} 筆，共 {unmatchedPagination.total} 筆
              </div>
              {unmatchedPagination.totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">第 {currentUnmatchedPage} / {unmatchedPagination.totalPages} 頁</span>
                  <button
                    onClick={() => setUnmatchedPage(Math.max(1, currentUnmatchedPage - 1))}
                    disabled={currentUnmatchedPage === 1}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setUnmatchedPage(Math.min(unmatchedPagination.totalPages, currentUnmatchedPage + 1))}
                    disabled={currentUnmatchedPage === unmatchedPagination.totalPages}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {unmatchedLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : unmatchedRecords.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {unmatchedFilter.length > 0 || unmatchedMonth ? '找不到符合條件的記錄' : '尚無銀行存摺明細'}
          </div>
        ) : (
          <>
            {/* 上方水平捲動條（與表格同步） */}
            <div
              ref={topScrollRef}
              onScroll={handleTopScroll}
              className="overflow-x-auto overflow-y-hidden"
              style={{ height: 14 }}
            >
              <div style={{ width: tableScrollWidth, height: 1 }} />
            </div>
            <div ref={tableScrollRef} onScroll={handleTableScroll} className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedRecords.length === unmatchedRecords.filter(r => r.status === 'PENDING').length && pendingRecords.length > 0}
                        onChange={() => {
                          if (selectedRecords.length === pendingRecords.length) {
                            setSelectedRecords([])
                          } else {
                            setSelectedRecords(pendingRecords.map(r => r.id))
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      交易日期
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      借/貸
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      輸入行別
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      交易摘要
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      金額
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      虛擬帳號
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      備註
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      狀態
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {unmatchedRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRecords.includes(record.id)}
                          onChange={() => toggleSelectRecord(record.id)}
                          disabled={record.status !== 'PENDING'}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(record.transactionDate)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${(record as any).direction === 'DEBIT' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {(record as any).direction === 'DEBIT' ? '支出' : '存入'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center font-mono">
                        {getInputType(record.rawData) || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getTransactionSummary(record.rawData) || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(record.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {getVirtualAccount(record.rawData) || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {record.memo ? (
                          <div className="group relative max-w-[160px]" title={record.memo}>
                            <div className="truncate">{record.memo}</div>
                            <div className="pointer-events-none absolute right-0 bottom-full mb-2 hidden group-hover:block z-30 whitespace-pre-wrap break-words w-80 bg-gray-900 text-white text-xs rounded-md px-3 py-2 shadow-lg">
                              {record.memo}
                            </div>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {(() => {
                          const va = getVirtualAccount(record.rawData)
                          const display = getDupDisplay(record.id, va, record.status, record.resolveNote)
                          // 1) 已指派月份 / 重複繳款：直接取代一般狀態
                          if (display.kind === 'reassigned' || display.kind === 'duplicate') {
                            const badge = renderDupBadge(display, record.matchedBill?.unit?.unitCode, record.matchedBill?.billingPeriod)
                            return (
                              <button
                                type="button"
                                onClick={() => setMatchedDetailRecord(record)}
                                className="cursor-pointer hover:opacity-80"
                                title="點擊查看匹配詳情"
                              >
                                {badge}
                              </button>
                            )
                          }
                          // 2) 一般（含 duplicate group 第一筆 MATCHED）→ 原本樣式
                          return record.status === 'MATCHED' ? (
                            <button
                              type="button"
                              onClick={() => setMatchedDetailRecord(record)}
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium hover:opacity-80 cursor-pointer ${unmatchedStatusColors[record.status]}`}
                              title="點擊查看匹配詳情"
                            >
                              {unmatchedStatusLabels[record.status]}
                              {record.matchedBill?.unit?.unitCode && (
                                <span className="ml-1 font-mono">· {record.matchedBill.unit.unitCode}</span>
                              )}
                            </button>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${unmatchedStatusColors[record.status]}`}>
                              {unmatchedStatusLabels[record.status]}
                            </span>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {(() => {
                          const va = getVirtualAccount(record.rawData)
                          const isDup = !!(va && duplicateVASet.has(va))
                          const canReassign = isDup && (record.status === 'PENDING' || record.status === 'MATCHED')
                          return (
                            <>
                              {canReassign && (
                                <button
                                  onClick={() => openReassignModal(record)}
                                  className="text-orange-600 hover:text-orange-800 mr-3"
                                  title="重新指派到其他帳單"
                                >
                                  <ArrowsRightLeftIcon className="w-4 h-4" />
                                </button>
                              )}
                              {record.status === 'PENDING' && (
                                <>
                                  <button
                                    onClick={() => openResolveModal(record)}
                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                    title="處理（匹配帳單 / 標記廠商款項）"
                                  >
                                    <CheckCircleIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUnmatchedRecord(record.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title="刪除"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {record.status !== 'PENDING' && record.status !== 'MATCHED' && (
                                <button
                                  onClick={() => handleDeleteUnmatchedRecord(record.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="刪除"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {unmatchedPagination && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  顯示第 {((currentUnmatchedPage - 1) * currentUnmatchedLimit) + 1} - {Math.min(currentUnmatchedPage * currentUnmatchedLimit, unmatchedPagination.total)} 筆，共 {unmatchedPagination.total} 筆
                </div>
                {unmatchedPagination.totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">第 {currentUnmatchedPage} / {unmatchedPagination.totalPages} 頁</span>
                    <button
                      onClick={() => setUnmatchedPage(Math.max(1, currentUnmatchedPage - 1))}
                      disabled={currentUnmatchedPage === 1}
                      className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setUnmatchedPage(Math.min(unmatchedPagination.totalPages, currentUnmatchedPage + 1))}
                      disabled={currentUnmatchedPage === unmatchedPagination.totalPages}
                      className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* 銀行對帳操作紀錄 */}
      <FinanceAuditLog section="reconciliation" title="對帳操作紀錄" limit={50} />

      {/* Resolve Modal - 把存摺紀錄人工匹配到未付帳單 */}
      {showResolveModal && resolvingRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-lg bg-white max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">處理未匹配記錄</h3>
                <p className="text-sm text-gray-500 mt-1">將這筆銀行存摺紀錄匹配到帳單，或標記為廠商款項</p>
              </div>
              <button onClick={closeResolveModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* 存摺紀錄摘要 */}
            <div className="bg-blue-50 border border-blue-100 p-3 rounded-md mb-3 flex items-center justify-between flex-wrap gap-y-1">
              <div className="flex items-center space-x-4 text-sm">
                <div>
                  <span className="text-gray-500">借貸：</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${resolvingRecord.direction === 'DEBIT' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    {resolvingRecord.direction === 'DEBIT' ? '支出' : '存入'}
                  </span>
                </div>
                <div><span className="text-gray-500">日期：</span>{formatDate(resolvingRecord.transactionDate)}</div>
                <div><span className="text-gray-500">金額：</span><span className="font-bold text-lg text-blue-800">{formatCurrency(resolvingRecord.amount)}</span></div>
                {getVirtualAccount(resolvingRecord.rawData) && (
                  <div><span className="text-gray-500">虛擬帳號：</span><span className="font-mono">{getVirtualAccount(resolvingRecord.rawData)}</span></div>
                )}
              </div>
              {resolvingRecord.memo && (
                <div className="text-xs text-gray-600 max-w-full truncate" title={resolvingRecord.memo}>備註：{resolvingRecord.memo}</div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-3">
              <button
                type="button"
                disabled={resolvingRecord.direction === 'DEBIT'}
                onClick={async () => {
                  setResolveTab('BILL')
                  if (resolveCandidates.length === 0) await loadResolveCandidates(resolvingRecord, resolveSearch, resolvePeriod)
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  resolveTab === 'BILL'
                    ? 'border-blue-600 text-blue-700'
                    : resolvingRecord.direction === 'DEBIT'
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                title={resolvingRecord.direction === 'DEBIT' ? '支出記錄無法匹配住戶帳單' : ''}
              >
                匹配住戶帳單
              </button>
              <button
                type="button"
                onClick={async () => {
                  setResolveTab('VENDOR')
                  if (resolveVendors.length === 0) await loadVendorsForResolve(resolvingRecord.direction || 'CREDIT')
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  resolveTab === 'VENDOR' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                標記廠商款項
              </button>
              <button
                type="button"
                onClick={() => setResolveTab('OTHER')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  resolveTab === 'OTHER' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                其他
              </button>
            </div>

            {resolveTab === 'BILL' && (
            <>
            {/* 帳期 + 搜尋 */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <label className="text-sm text-gray-600 whitespace-nowrap">帳期</label>
              <input
                type="month"
                value={resolvePeriod}
                onChange={(e) => {
                  setResolvePeriod(e.target.value)
                  loadResolveCandidates(resolvingRecord, resolveSearch, e.target.value)
                }}
                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => {
                  setResolvePeriod('')
                  loadResolveCandidates(resolvingRecord, resolveSearch, '')
                }}
                disabled={!resolvePeriod || resolveCandidatesLoading}
                className="px-2 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
                title="顯示所有帳期的未繳帳單（遲繳時使用）"
              >
                全部帳期
              </button>
              <input
                type="text"
                value={resolveSearch}
                onChange={(e) => setResolveSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadResolveCandidates(resolvingRecord, resolveSearch, resolvePeriod) }}
                placeholder="輸入戶號/地址篩選候選帳單（留空則依金額接近度排序）..."
                className="flex-1 min-w-[200px] px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => loadResolveCandidates(resolvingRecord, resolveSearch, resolvePeriod)}
                disabled={resolveCandidatesLoading}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                搜尋
              </button>
              <span className="text-xs text-gray-500">只顯示「未繳」或「ACH 失敗」的帳單</span>
            </div>

            {/* 候選帳單清單 */}
            <div className="border border-gray-200 rounded-md flex-1 overflow-auto">
              {resolveCandidatesLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : resolveCandidates.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">找不到可匹配的帳單</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left"></th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">戶號</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">地址</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">繳費人</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">帳期</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-500">應繳</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {resolveCandidates.map((b: any) => {
                      const diff = Number(b.totalAmount) - Number(resolvingRecord.amount)
                      const exact = diff === 0
                      return (
                        <tr
                          key={b.id}
                          className={`cursor-pointer hover:bg-blue-50 ${resolveSelectedBillId === b.id ? 'bg-blue-100' : ''}`}
                          onClick={() => setResolveSelectedBillId(b.id)}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="radio"
                              checked={resolveSelectedBillId === b.id}
                              onChange={() => setResolveSelectedBillId(b.id)}
                            />
                          </td>
                          <td className="px-3 py-2 font-mono">{b.unitCode}</td>
                          <td className="px-3 py-2 text-gray-600">{b.address || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{b.payerName || '-'}</td>
                          <td className="px-3 py-2">{b.billingPeriod}</td>
                          <td className={`px-3 py-2 text-right whitespace-nowrap ${exact ? 'text-green-700 font-semibold' : 'text-gray-900'}`}>
                            {formatCurrency(Number(b.totalAmount))}
                            {!exact && (
                              <span className="ml-1 text-xs text-gray-400">({diff > 0 ? '+' : ''}{formatCurrency(diff)})</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>

            </>
            )}

            {resolveTab === 'VENDOR' && (
            <>
              {/* 廠商搜尋 */}
              <div className="flex items-center space-x-2 mb-3">
                <input
                  type="text"
                  value={resolveVendorSearch}
                  onChange={(e) => setResolveVendorSearch(e.target.value)}
                  placeholder="輸入廠商名稱／標籤關鍵字篩選..."
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-500">
                  僅顯示「{resolvingRecord.direction === 'DEBIT' ? '支出' : '收入'}」類型廠商
                </span>
              </div>

              <div className="border border-gray-200 rounded-md flex-1 overflow-auto">
                {resolveVendorsLoading ? (
                  <div className="p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : (() => {
                  const q = resolveVendorSearch.trim().toLowerCase()
                  const filtered = q
                    ? resolveVendors.filter(v =>
                        v.name.toLowerCase().includes(q) ||
                        v.tags.some(t => t.toLowerCase().includes(q))
                      )
                    : resolveVendors
                  if (resolveVendors.length === 0) {
                    const typeLabel = resolvingRecord.direction === 'DEBIT' ? '支出' : '收入'
                    return (
                      <div className="p-6 text-center text-gray-500 text-sm space-y-2">
                        <div>此專案目前沒有「{typeLabel}」類型的廠商。</div>
                        <div className="text-xs text-gray-400">
                          「{resolvingRecord.direction === 'DEBIT' ? '支出' : '存入'}」記錄只能標記給「{typeLabel}」類型的廠商。
                        </div>
                        <Link to={`/project/${projectKey}/finance/vendors`} className="inline-block text-blue-600 hover:underline">
                          前往廠商管理新增「{typeLabel}」廠商 →
                        </Link>
                      </div>
                    )
                  }
                  if (filtered.length === 0) {
                    return <div className="p-6 text-center text-gray-500 text-sm">找不到符合「{resolveVendorSearch.trim()}」的廠商。</div>
                  }
                  return (
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left"></th>
                          <th className="px-3 py-2 text-left text-xs text-gray-500">廠商名稱</th>
                          <th className="px-3 py-2 text-left text-xs text-gray-500">標籤</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filtered.map(v => (
                          <tr
                            key={v.id}
                            className={`cursor-pointer hover:bg-blue-50 ${resolveSelectedVendorId === v.id ? 'bg-blue-100' : ''}`}
                            onClick={() => setResolveSelectedVendorId(v.id)}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="radio"
                                checked={resolveSelectedVendorId === v.id}
                                onChange={() => setResolveSelectedVendorId(v.id)}
                              />
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-900">{v.name}</td>
                            <td className="px-3 py-2">
                              {v.tags.length === 0 ? <span className="text-gray-400">-</span> : (
                                <div className="flex flex-wrap gap-1">
                                  {v.tags.map(t => (
                                    <span key={t} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">{t}</span>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                })()}
              </div>
            </>
            )}

            {resolveTab === 'OTHER' && (
              <div className="flex-1 overflow-auto p-4 border border-gray-200 rounded-md bg-gray-50">
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    若這筆銀行存摺紀錄不屬於住戶帳單、也不屬於廠商款項（例如人工調整、誤入帳、其他內部款項…），
                    可以在這裡填寫處理原因，標記為「已處理」（不影響任何帳單或廠商資料）。
                  </p>
                </div>
              </div>
            )}

            {/* 備註與操作 */}
            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">
                {resolveTab === 'OTHER' ? (
                  <>處理原因 <span className="text-red-500">*</span></>
                ) : (
                  <>備註（選填）</>
                )}
              </label>
              {resolveTab === 'OTHER' ? (
                <textarea
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  placeholder="請填寫手動標記為已處理的原因（必填）…"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <input
                  type="text"
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  placeholder={resolveTab === 'BILL'
                    ? `人工匹配：存摺 ${formatDate(resolvingRecord.transactionDate)} ${formatCurrency(resolvingRecord.amount)}`
                    : `廠商款項說明…`}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>

            <div className="flex justify-end items-center mt-4">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={closeResolveModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                {resolveTab === 'BILL' && (
                  <button
                    type="button"
                    onClick={handleResolveSubmit}
                    disabled={!resolveSelectedBillId || resolveSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {resolveSubmitting ? '匹配中...' : '確認匹配'}
                  </button>
                )}
                {resolveTab === 'VENDOR' && (
                  <button
                    type="button"
                    onClick={handleResolveAsVendor}
                    disabled={!resolveSelectedVendorId || resolveSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {resolveSubmitting ? '處理中...' : '標記為廠商款項'}
                  </button>
                )}
                {resolveTab === 'OTHER' && (
                  <button
                    type="button"
                    onClick={handleResolveAsOther}
                    disabled={!resolveNote.trim() || resolveSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {resolveSubmitting ? '處理中...' : '標記為已處理'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matched Records Modal */}
      {showMatchedModal && selectedUpload && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div>
                <h3 className="text-lg font-medium text-gray-900">已匹配記錄詳情</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedUpload.fileName} - 共 {matchedPagination.total} 筆匹配記錄
                </p>
              </div>
              <button
                onClick={closeMatchedModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {matchedRecordsLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">載入中...</p>
                </div>
              ) : matchedRecords.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  沒有匹配記錄
                </div>
              ) : (
                <div className="space-y-2">
                  {matchedRecords.map((record) => (
                    <div
                      key={record.id}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Summary row - always visible */}
                      <div
                        onClick={() => toggleRecordExpand(record.id)}
                        className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                      >
                        <div className="flex items-center space-x-6 flex-1">
                          <div className="min-w-[80px]">
                            <span className="font-mono text-sm font-medium text-gray-900">{record.unitCode}</span>
                          </div>
                          <div className="min-w-[100px]">
                            <span className="text-sm text-gray-600">{record.contactName || '-'}</span>
                          </div>
                          <div className="min-w-[80px]">
                            <span className="text-sm text-gray-500">{record.billingPeriod}</span>
                          </div>
                          <div className="min-w-[100px] text-right">
                            <span className={`text-sm font-medium ${
                              Number(record.paidAmount) === Number(record.totalAmount)
                                ? 'text-green-600'
                                : 'text-yellow-600'
                            }`}>
                              {formatCurrency(record.paidAmount)}
                            </span>
                          </div>
                          <div className="min-w-[140px]">
                            <span className="text-xs text-gray-500">
                              {record.paidAt ? formatDate(record.paidAt) : '-'}
                            </span>
                          </div>
                          {record.bankRecord?.virtualAccount && (
                            <div className="min-w-[140px]">
                              <span className="font-mono text-xs text-gray-600">
                                {record.bankRecord.virtualAccount}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <ChevronRightIcon
                            className={`w-5 h-5 text-gray-400 transition-transform ${expandedRecords.has(record.id) ? 'rotate-90' : ''}`}
                          />
                        </div>
                      </div>

                      {/* Expanded details */}
                      {expandedRecords.has(record.id) && (
                        <div className="px-4 py-3 bg-white border-t border-gray-200">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500 text-xs">戶號</div>
                              <div className="font-mono font-medium">{record.unitCode}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">地址</div>
                              <div className="truncate" title={record.address}>{record.address || '-'}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">繳款人</div>
                              <div>{record.contactName || '-'}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">聯絡電話</div>
                              <div>{record.contactPhone || '-'}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">帳單期別</div>
                              <div>{record.billingPeriod}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">應繳金額</div>
                              <div>{formatCurrency(record.totalAmount)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">實繳金額</div>
                              <div className={`font-medium ${
                                Number(record.paidAmount) === Number(record.totalAmount)
                                  ? 'text-green-600'
                                  : 'text-yellow-600'
                              }`}>{formatCurrency(record.paidAmount)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">繳款管道</div>
                              <div>{record.paymentChannel || '-'}</div>
                            </div>
                            {record.bankRecord && (
                              <>
                                <div>
                                  <div className="text-gray-500 text-xs">虛擬帳號</div>
                                  <div className="font-mono">{record.bankRecord.virtualAccount || '-'}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500 text-xs">繳款人姓名</div>
                                  <div>{record.bankRecord.payerName || '-'}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500 text-xs">入帳日</div>
                                  <div>{record.bankRecord.depositDate || '-'}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500 text-xs">繳款日</div>
                                  <div>{record.bankRecord.paymentDate || '-'}</div>
                                </div>
                              </>
                            )}
                            <div>
                              <div className="text-gray-500 text-xs">銷帳時間</div>
                              <div>{record.reconciledAt ? formatDate(record.reconciledAt) : '-'}</div>
                            </div>
                          </div>

                          {/* Bank record details/notes if available */}
                          {record.bankRecord?.details && record.bankRecord.details.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <div className="text-gray-500 text-xs mb-1">繳款明細</div>
                              <div className="text-sm text-gray-700">
                                {record.bankRecord.details.filter(d => d).join(', ')}
                              </div>
                            </div>
                          )}

                          {record.bankRecord?.notes && record.bankRecord.notes.length > 0 && record.bankRecord.notes.some(n => n) && (
                            <div className="mt-2">
                              <div className="text-gray-500 text-xs mb-1">備註</div>
                              <div className="text-sm text-gray-700">
                                {record.bankRecord.notes.filter(n => n).join(', ')}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {matchedPagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4 flex-shrink-0">
                <div className="text-sm text-gray-700">
                  第 {matchedPagination.page} / {matchedPagination.totalPages} 頁，共 {matchedPagination.total} 筆
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => loadMoreMatchedRecords(matchedPagination.page - 1)}
                    disabled={matchedPagination.page === 1 || matchedRecordsLoading}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => loadMoreMatchedRecords(matchedPagination.page + 1)}
                    disabled={matchedPagination.page === matchedPagination.totalPages || matchedRecordsLoading}
                    className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4 flex-shrink-0">
              <button
                onClick={closeMatchedModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 未匹配記錄 Modal */}
      {unmatchedModalUpload && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-5xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">未匹配記錄</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {unmatchedModalUpload.fileName} - 共 {unmatchedModalUpload.unmatchedCount} 筆
                </p>
              </div>
              <button onClick={closeUnmatchedModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {unmatchedModalLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : unmatchedModalRecords.length === 0 ? (
                <div className="p-6 text-center text-gray-500">沒有資料</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">交易日期</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">借/貸</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">金額</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">虛擬帳號</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">備註</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {unmatchedModalRecords.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{formatDate(r.transactionDate)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${(r as any).direction === 'DEBIT' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {(r as any).direction === 'DEBIT' ? '支出' : '存入'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">{formatCurrency(r.amount)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 font-mono">{getVirtualAccount(r.rawData) || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate" title={r.memo || ''}>{r.memo || '-'}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => { closeUnmatchedModal(); openResolveModal(r) }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                          >
                            處理
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex justify-end pt-4">
              <button onClick={closeUnmatchedModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">關閉</button>
            </div>
          </div>
        </div>
      )}

      {/* 略過記錄 Modal */}
      {skippedModalUpload && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-5xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">已略過記錄</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {skippedModalUpload.fileName} - 共 {skippedModalUpload.skippedCount} 筆
                </p>
              </div>
              <button onClick={closeSkippedModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {skippedLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : skippedRecords.length === 0 ? (
                <div className="p-6 text-center text-gray-500">沒有資料</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">交易日期</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">金額</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">虛擬帳號</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">備註</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">略過原因</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {skippedRecords.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{formatDate(r.transactionDate)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">{formatCurrency(r.amount)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 font-mono">{getVirtualAccount(r.rawData) || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate">{r.memo || '-'}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {r.skipReason || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex justify-end pt-4">
              <button onClick={closeSkippedModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">關閉</button>
            </div>
          </div>
        </div>
      )}

      {/* 重新指派 Modal */}
      {reassignRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">重新指派到帳單</h3>
                <p className="text-sm text-gray-500 mt-1">
                  將這筆重複繳款改為消除其他月份的帳單
                </p>
              </div>
              <button onClick={closeReassignModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="bg-orange-50 border border-orange-200 p-3 rounded-md text-sm mb-4">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-500">交易日期：</span>{formatDate(reassignRecord.transactionDate)}</div>
                <div><span className="text-gray-500">金額：</span><span className="font-medium">{formatCurrency(reassignRecord.amount)}</span></div>
                <div className="col-span-2"><span className="text-gray-500">虛擬帳號：</span><span className="font-mono">{getVirtualAccount(reassignRecord.rawData) || '-'}</span></div>
                {reassignRecord.matchedBill && (
                  <div className="col-span-2 text-orange-700">
                    <span className="text-gray-500">目前配對：</span>
                    <span className="font-mono">{reassignRecord.matchedBill.unit?.unitCode}</span>
                    <span className="ml-2">{reassignRecord.matchedBill.billingPeriod}</span>
                    <span className="ml-2 text-xs">（改指派後此帳單會還原為未繳）</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={reassignSearch}
                  onChange={(e) => setReassignSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') loadReassignBills(reassignSearch) }}
                  placeholder="搜尋戶號或地址..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <button
                  onClick={() => loadReassignBills(reassignSearch)}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  搜尋
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">只顯示「未繳」或「ACH 失敗」的帳單</p>
            </div>

            <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md">
              {reassignLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : reassignBills.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">找不到可匹配的帳單</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left"></th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">戶號</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">地址</th>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">帳期</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-500">應繳</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reassignBills.map(b => (
                      <tr
                        key={b.id}
                        className={`cursor-pointer hover:bg-blue-50 ${reassignSelectedBillId === b.id ? 'bg-blue-100' : ''}`}
                        onClick={() => setReassignSelectedBillId(b.id)}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="radio"
                            checked={reassignSelectedBillId === b.id}
                            onChange={() => setReassignSelectedBillId(b.id)}
                          />
                        </td>
                        <td className="px-3 py-2 font-mono">{b.unitCode}</td>
                        <td className="px-3 py-2 text-gray-600">{b.address || '-'}</td>
                        <td className="px-3 py-2">{b.billingPeriod}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(Number(b.totalAmount))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={closeReassignModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleReassignSubmit}
                disabled={!reassignSelectedBillId || reassignSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reassignSubmitting ? '處理中...' : '確認重新指派'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 單筆匹配詳情 Modal */}
      {matchedDetailRecord && (() => {
        const mb = matchedDetailRecord.matchedBill
        let bankRec: any = null
        if (mb?.bankRecord) {
          try { bankRec = typeof mb.bankRecord === 'string' ? JSON.parse(mb.bankRecord) : mb.bankRecord } catch { bankRec = null }
        }
        return (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-lg bg-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">已匹配記錄詳情</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {mb?.unit?.unitCode || '-'} - {mb?.billingPeriod || '-'}
                  </p>
                </div>
                <button
                  onClick={() => setMatchedDetailRecord(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Bill Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500 text-xs">戶號</div>
                    <div className="font-mono font-medium">{mb?.unit?.unitCode || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">地址</div>
                    <div className="truncate" title={mb?.unit?.address || ''}>{mb?.unit?.address || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">繳款人</div>
                    <div>{mb?.unit?.payer?.name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">聯絡電話</div>
                    <div>{mb?.unit?.payer?.phone || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">帳單期別</div>
                    <div>{mb?.billingPeriod || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">應繳金額</div>
                    <div>{formatCurrency(Number(mb?.totalAmount || 0))}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">實繳金額</div>
                    <div className={`font-medium ${
                      mb && Number(mb.paidAmount) === Number(mb.totalAmount)
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    }`}>{formatCurrency(Number(mb?.paidAmount || 0))}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">繳款管道</div>
                    <div>{mb?.paymentChannel || bankRec?.paymentChannel || '-'}</div>
                  </div>
                </div>

                {/* Bank Record Details */}
                {bankRec && (
                  <>
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">銀行對帳資訊</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 text-xs">虛擬帳號</div>
                          <div className="font-mono">{bankRec.virtualAccount || getVirtualAccount(matchedDetailRecord.rawData) || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">繳款人姓名</div>
                          <div>{bankRec.payerName || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">入帳日</div>
                          <div>{bankRec.depositDate || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">繳款日</div>
                          <div>{bankRec.paymentDate || '-'}</div>
                        </div>
                        {bankRec.amount !== undefined && bankRec.amount !== null && (
                          <div>
                            <div className="text-gray-500 text-xs">銀行金額</div>
                            <div className="font-medium">{formatCurrency(Number(bankRec.amount))}</div>
                          </div>
                        )}
                        <div>
                          <div className="text-gray-500 text-xs">交易日期</div>
                          <div>{formatDate(matchedDetailRecord.transactionDate)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">輸入行別</div>
                          <div className="font-mono">{getInputType(matchedDetailRecord.rawData) || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">交易摘要</div>
                          <div className="truncate" title={getTransactionSummary(matchedDetailRecord.rawData) || ''}>{getTransactionSummary(matchedDetailRecord.rawData) || '-'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 text-xs">銷帳時間</div>
                          <div>{mb?.reconciledAt ? formatDate(mb.reconciledAt) : '-'}</div>
                        </div>
                        {mb?.paidAt && (
                          <div>
                            <div className="text-gray-500 text-xs">繳款時間</div>
                            <div>{formatDate(mb.paidAt)}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details and Notes */}
                    {bankRec.details && bankRec.details.length > 0 && bankRec.details.some((d: string) => d) && (
                      <div className="border-t border-gray-100 pt-3">
                        <div className="text-gray-500 text-xs mb-1">繳款明細</div>
                        <div className="text-sm text-gray-700">
                          {bankRec.details.filter((d: string) => d).join(', ')}
                        </div>
                      </div>
                    )}

                    {bankRec.notes && bankRec.notes.length > 0 && bankRec.notes.some((n: string) => n) && (
                      <div className="pt-2">
                        <div className="text-gray-500 text-xs mb-1">備註</div>
                        <div className="text-sm text-gray-700">
                          {bankRec.notes.filter((n: string) => n).join(', ')}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!bankRec && (
                  <div className="border-t border-gray-200 pt-4 text-center text-gray-500 text-sm">
                    無銀行對帳資料
                  </div>
                )}

                {matchedDetailRecord.resolveNote && (
                  <div className="border-t border-gray-100 pt-3 text-sm text-gray-600">
                    <span className="text-gray-500">人工備註：</span>{matchedDetailRecord.resolveNote}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => setMatchedDetailRecord(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  關閉
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

export default BankReconciliation
